import { forwardRef, ForwardedRef, useState, ChangeEvent, RefObject, useEffect } from 'react';

import iconHidePassword from '../assets/eye.svg';
import iconNotHidePassword from '../assets/eye-slash.svg';
import { useTranslation } from 'react-i18next';

interface InputProp {
  label?: string,
  placeholder?: string,
  icon?: string,
  isPassword?: boolean,
  maxLength?: number,
  checkContent?: ( text: string ) => string | null,
  onChange?: () => void,
};

export const Input = forwardRef( function Input( { label, placeholder, icon, checkContent, onChange, isPassword = false, maxLength }: InputProp, ref: ForwardedRef<HTMLInputElement> ) {
  const { t } = useTranslation();

  const [ isFocus, setIsFocus ] = useState<boolean>( false );
  const [ isHide, setIsHide ] = useState<boolean>( true );
  const [ errorMessage, setErrorMessage ] = useState<string>( '' );

  useEffect( () => {
    if ( ref && checkContent != undefined ) {
      const input = ref as RefObject<HTMLInputElement>;
      if ( input.current ) {
        const error = checkContent( input.current.value );
        setErrorMessage( error ?? '' );
      }
    }
  }, [] );

  return (
    <div className='flex flex-col gap-[2px]'>
      <p className='ml-1 text-base'>
        {label}
      </p>
      <div
        className={
          'flex items-center rounded-lg py-1 border min-w-[150px] ' +
          ( icon ? 'pr-2 ' : 'px-2 ' ) +
          ( isFocus ? 'border-2 border-secondary m-[-1px] ' : '' )
        }
      >
        { icon ?
          <img
            src={icon}
            className='w-6 h-6 mx-2 pr-2 select-none border-r '
            draggable="false"
          />
          : null
        }
        <input
          style={{ outline: 'none' }}
          ref={ref}
          className='w-full select-none caret-secondary'
          placeholder={ isPassword && isHide ? '•'.repeat( String( placeholder ).length ) : placeholder }
          type={isPassword && isHide ? 'password' : 'text'}
          maxLength={ maxLength }
          onFocus={ () => { setIsFocus( true ); } }
          onBlur={ () => { setIsFocus( false ); } }
          onChange={ ( event: ChangeEvent ) => {
            if ( onChange != undefined ) {
              onChange();
            }
            if ( checkContent != undefined ) {
              setErrorMessage( checkContent( ( event.currentTarget as HTMLInputElement ).value ) ?? '' );
            }
          } }
        />
        { isPassword ?
          <img
            src={ isHide ? iconHidePassword : iconNotHidePassword }
            onClick={ () => { setIsHide( !isHide ); } }
            className='w-5 h-5 select-none ml-2'
            draggable="false"
          />
          : null
        }
      </div>
      {
        checkContent !== undefined && errorMessage ?
          <p className='h-[12px] text-[10px] text-error'>
            { t( errorMessage ) }
          </p>
          :
          null
      }
    </div>
  );
} );